import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categoryData';
import { hotLinks, latestUpdates } from '@/mocks/homeData';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const allItems = useMemo(() => {
    const items = [...hotLinks, ...latestUpdates].filter(
      (item) => {
        if (!category) return true;
        const catSlugMap: Record<string, string> = {
          '综艺娱乐': 'zongyi',
          '电影资源': 'dianying',
          '电视剧': 'dianshiju',
          '动漫动画': 'dongman',
          '纪录片': 'jilupian',
          '体育赛事': 'tiyu',
          '音乐MV': 'yinyue',
          '学习教育': 'xuexi',
        };
        return catSlugMap[item.category] === category.slug;
      }
    );
    return items;
  }, [category]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category ? `${category.name} - 福利视频网址大全` : '分类资源 - 福利视频网址大全',
    description: category
      ? `浏览${category.name}相关的所有免费高清视频资源，共${category.count}个可用网址入口`
      : '按分类浏览所有免费高清视频资源入口',
    url: `/category/${slug}`,
  };

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <main>
        <div className="pt-20 pb-14 md:pb-20 bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category?.name || '分类' },
            ]}
          />

          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 mt-8">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {category ? category.name : '分类资源'}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base">
                  {category
                    ? `共收录 ${category.count} 个${category.name}相关福利视频网址资源`
                    : '按分类浏览所有视频资源'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-10 justify-center">
                <Link
                  to="/category/zongyi"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'zongyi'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  综艺娱乐
                </Link>
                <Link
                  to="/category/dianying"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'dianying'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  电影资源
                </Link>
                <Link
                  to="/category/dianshiju"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'dianshiju'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  电视剧
                </Link>
                <Link
                  to="/category/dongman"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'dongman'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  动漫动画
                </Link>
                <Link
                  to="/category/jilupian"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'jilupian'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  纪录片
                </Link>
                <Link
                  to="/category/tiyu"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'tiyu'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  体育赛事
                </Link>
                <Link
                  to="/category/yinyue"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'yinyue'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  音乐MV
                </Link>
                <Link
                  to="/category/xuexi"
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'xuexi'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-accent-100 border border-background-200'
                  }`}
                >
                  学习教育
                </Link>
              </div>

              {allItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {allItems.map((item, idx) => (
                    <Link
                      key={idx}
                      to={`/article/${item.id}`}
                      className="group bg-background-50 rounded-xl p-5 hover:shadow-md transition-all duration-300 cursor-pointer border border-background-200/70"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                          {item.tag}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {item.category}
                        </span>
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-accent-600 transition-colors line-clamp-2 leading-snug">
                        {item.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-400 mt-3 pt-3 border-t border-background-200">
                        {item.views && (
                          <span>
                            <i className="ri-eye-line mr-1"></i>{item.views}
                          </span>
                        )}
                        <span>
                          <i className="ri-calendar-line mr-1"></i>{item.date}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-400 text-sm">该分类暂无资源，请查看其他分类</p>
                  <Link
                    to="/"
                    className="inline-block mt-4 text-sm text-accent-600 hover:text-accent-700 cursor-pointer"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}